<template>
  <a-radio-group
    v-model:value="innerValue"
    :disabled="disabled"
    :option-type="optionType"
    :button-style="buttonStyle"
    :size="size"
    :name="name"
    class="ant-radio-group-wrap"
    @change="onChange"
  >
    <template v-if="normalizedOptions.length">
      <a-radio
        v-for="opt in normalizedOptions"
        :key="String(opt.value)"
        :value="opt.value"
        :disabled="opt.disabled"
      >
        {{ opt.label }}
      </a-radio>
    </template>
    <slot v-else />
  </a-radio-group>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import type { SelectOption } from "./types";

interface Props {
  modelValue?: string | number | boolean;
  options?: SelectOption[];
  disabled?: boolean;
  size?: SizeType;
  name?: string;
  /** default | button */
  optionType?: "default" | "button";
  buttonStyle?: "outline" | "solid";
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  options: () => [],
  disabled: false,
  size: "middle",
  name: undefined,
  optionType: "default",
  buttonStyle: "outline",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | boolean): void;
  (e: "change", value: string | number | boolean): void;
}>();

const normalizedOptions = computed(() => props.options || []);

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: string | number | boolean) => {
    emit("update:modelValue", val);
  },
});

const onChange = (e: any) => {
  const val = e?.target?.value ?? e;
  emit("update:modelValue", val);
  // Giữ event gốc để tương thích handler kiểu e.target.value
  emit("change", e);
};
</script>

<style scoped>
.ant-radio-group-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}
</style>

<!--
  Hướng dẫn sử dụng — Radio
  Global: <h-radio v-model="gender" :options="options" />
           <h-radio v-model="tab" option-type="button" :options="options" />
  Import: import { Radio } from '@/components/ant/select'

  Props: modelValue, options, optionType (default|button), buttonStyle, size, disabled
  Events: update:modelValue, change
  Docs: src/components/ant/select/README.md
-->
